import { PERMISSION_LIMIT } from "@/utils/permissions"

interface UserPermissionResult {
    permissionId: string
    isActive: boolean
    effectiveRange?: string[]
    statusBlacklist?: string[]
    statusWhitelist?: string[]
}

export interface UserPermissions {
    results: UserPermissionResult[]
}

interface UserInfo {
    uuid: string
    departmentUuid?: string
    level: number
}

interface UserStoreShape {
    formatUserPermissions: string[]
    userPermissions: UserPermissions | null
    userInfo: UserInfo | null
}

interface AssetData {
    department?: string
    status?: string
    creatorUuid?: string
    roleLevel?: number
}

interface CheckPermissionConfig {
    requiredAll?: boolean
    requireValidateAssets?: boolean
}

/**
 * 驗證用戶是否具備當前物件的操控權限，權限需要包含以下（全面驗證權限，包含狀態、部門、額外限制）
 * 具備權限
 * 符合編輯限制（例如：部門、本人）
 * 符合物件狀態限制（例如：草稿）
 * @param {string | string[]} requiredPermission 當前要符合的權限
 * @param {UserStoreShape} userStore userStore
 * @param {AssetData | null} assetData 資產物件
 * @param {CheckPermissionConfig} config config
 * @returns {boolean} 是否具備權限
 */
export default function checkPermissionConfig(
    requiredPermission: string | string[],
    userStore: UserStoreShape,
    assetData: AssetData | null,
    {
        requiredAll = true,
        requireValidateAssets = true,
    }: CheckPermissionConfig = {}
): boolean {
    const requiredPermissions = Array.isArray(requiredPermission)
        ? requiredPermission
        : [requiredPermission]

    const results = requiredPermissions.map((permission) => {
        const hasPermission = checkOnePermissions(
            permission,
            userStore.formatUserPermissions
        )
        if (!hasPermission) return false

        if (!requireValidateAssets) return true

        const effectiveRangeRestriction = getEffectiveRangeRestriction(
            permission,
            userStore.userPermissions
        )
        if (
            !validateRangeRestrictions(
                effectiveRangeRestriction,
                assetData,
                userStore.userInfo
            )
        )
            return false

        const statusWhitelistRestriction = getStatusWhitelistRestriction(
            permission,
            userStore.userPermissions
        )
        const statusBlacklistRestriction = getStatusBlacklistRestriction(
            permission,
            userStore.userPermissions
        )

        return validateStatusRestrictions(
            statusWhitelistRestriction,
            statusBlacklistRestriction,
            assetData?.status
        )
    })

    return requiredAll ? results.every(Boolean) : results.some(Boolean)
}

/**
 * 確認用戶具備此權限
 * @param {string} permission 要驗證是否擁有的權限
 * @param {string[]} userPermissions 用戶當前擁有的權限
 * @returns {boolean} 是否具備此權限
 */
export function checkOnePermissions(
    permission: string,
    userPermissions: string[]
): boolean {
    return userPermissions?.some((item) => item === permission) || false
}

/**
 * 取得該權限角色對應的限制條件
 * @param {string} requiredPermission 要驗證的條件
 * @param {UserPermissions} userStorePermissions userStorePermissions
 * @returns {string[]} 權限限制
 */
export function getEffectiveRangeRestriction(
    requiredPermission: string,
    userStorePermissions: UserPermissions | null
): string[] {
    if (!userStorePermissions) return []
    const permissions = userStorePermissions.results.filter(
        (item) => item.permissionId === requiredPermission
    )

    if (permissions.length === 0) return []

    const mergedEffectiveRange = permissions.reduce<string[]>(
        (acc, permission) => {
            if (permission.effectiveRange) {
                acc.push(...permission.effectiveRange)
            }
            return acc
        },
        []
    )

    return [...new Set(mergedEffectiveRange)]
}

/**
 * 根據對應的限制條件進行檢查
 * @param {string[]} restrictions restrictions
 * @param {AssetData | null} assetData assetData
 * @param {UserInfo} userInfo userInfo
 * @returns {boolean} 是否通過檢查
 */
function validateRangeRestrictions(
    restrictions: string[],
    assetData: AssetData | null,
    userInfo: UserInfo | null
): boolean {
    if (!restrictions || !userInfo) return true

    if (getIsLimitSamePeople(restrictions)) {
        if (assetData?.creatorUuid !== userInfo.uuid) return false
    }

    if (getIsLimitSameDepartment(restrictions)) {
        if (assetData?.department !== userInfo.departmentUuid) return false
    }

    if (restrictions.includes(PERMISSION_LIMIT.LOWER_LEVEL_DEPARTMENT)) {
        if ((assetData?.roleLevel ?? 0) >= userInfo.level) return false
    }

    if (restrictions.includes(PERMISSION_LIMIT.SAME_OR_LOWER_LEVEL)) {
        if ((assetData?.roleLevel ?? 0) > userInfo.level) return false
    }

    return true
}

/**
 * 該權限條件是否限制同個創建人
 * @param {string[]} effectiveRangeRestriction 權限限制
 * @returns {boolean} 是否限制
 */
function getIsLimitSamePeople(effectiveRangeRestriction: string[]): boolean {
    return effectiveRangeRestriction.some(
        (item) => item === PERMISSION_LIMIT.SAME_PEOPLE
    )
}

/**
 * 該權限條件是否限制同部門
 * @param {string[]} effectiveRangeRestriction 權限限制
 * @returns {boolean} 是否限制
 */
export function getIsLimitSameDepartment(
    effectiveRangeRestriction: string[]
): boolean {
    return effectiveRangeRestriction.some(
        (item) => item === PERMISSION_LIMIT.SAME_DEPARTMENT
    )
}

/**
 * 該權限條件是否是跨部門
 * @param {string[]} effectiveRangeRestriction 權限限制
 * @returns {boolean} 是否限制
 */
export function getIsCrossDepartment(
    effectiveRangeRestriction: string[]
): boolean {
    return effectiveRangeRestriction.some(
        (item) => item === PERMISSION_LIMIT.CROSS_DEPARTMENT
    )
}

/**
 * 該權限條件是否限制物件操作狀態（黑名單）
 * @param {string} requiredPermission 要驗證的條件
 * @param {UserPermissions} userStorePermissions userStorePermissions
 * @returns {string[]} 狀態黑名單
 */
function getStatusBlacklistRestriction(
    requiredPermission: string,
    userStorePermissions: UserPermissions | null
): string[] {
    if (!userStorePermissions) return []
    const permissions = userStorePermissions.results.filter(
        (item) => item.permissionId === requiredPermission
    )

    if (permissions.length === 0) return []

    const mergedStatusBlacklist = permissions.reduce<string[]>(
        (acc, permission) => {
            if (permission.statusBlacklist) {
                acc.push(...permission.statusBlacklist)
            }
            return acc
        },
        []
    )

    return [...new Set(mergedStatusBlacklist)]
}

/**
 * 該權限條件是否限制物件操作狀態（白名單）
 * @param {string} requiredPermission 要驗證的條件
 * @param {UserPermissions} userStorePermissions userStorePermissions
 * @returns {string[]} 狀態白名單
 */
function getStatusWhitelistRestriction(
    requiredPermission: string,
    userStorePermissions: UserPermissions | null
): string[] {
    if (!userStorePermissions) return []
    const permissions = userStorePermissions.results.filter(
        (item) => item.permissionId === requiredPermission
    )

    if (permissions.length === 0) return []

    const mergedStatusWhitelist = permissions.reduce<string[]>(
        (acc, permission) => {
            if (permission.statusWhitelist) {
                acc.push(...permission.statusWhitelist)
            }
            return acc
        },
        []
    )

    return [...new Set(mergedStatusWhitelist)]
}

/**
 * 檢查該權限是否限制物件操作狀態
 * @param {string[]} whitelist whitelist
 * @param {string[]} blacklist blacklist
 * @param {string | undefined} status status
 * @returns {boolean} 是否通過檢查
 */
function validateStatusRestrictions(
    whitelist: string[],
    blacklist: string[],
    status: string | undefined
): boolean {
    return (
        (!whitelist.length || (!!status && whitelist.includes(status))) &&
        (!blacklist.length || !status || !blacklist.includes(status))
    )
}
